import * as ortInstance from "onnxruntime-web/wasm"
import { defaultModelFetcher } from "./default-model-fetcher"
import {
  Model,
  ModelFactory,
  OrtConfigurer,
  Silero,
  SileroLegacy,
} from "./models"

export type ModelName = "v5" | "v6" | "legacy"

export const ort = ortInstance

const modelFiles: Record<ModelName, string> = {
  legacy: "silero_vad_legacy.onnx",
  v5: "silero_vad_v5.onnx",
  v6: "silero_vad_v6.onnx",
}

export interface LoadModelOptions {
  model: ModelName
  baseAssetPath: string
  onnxWASMBasePath: string
  ortConfig?: OrtConfigurer
}

export interface LoadedModel {
  model: Model
  /** Frame size the model expects. Differs between model versions. */
  frameSamples: 512 | 1536
  /** Duration of one frame at 16 kHz, the unit the ms-based options convert to. */
  msPerFrame: number
}

/**
 * Loads a Silero model and reports the frame size that goes with it. The two
 * are returned together because a FrameProcessor fed frames of the wrong size
 * would convert its ms options against the wrong frame rate.
 */
export async function loadModel(
  options: LoadModelOptions
): Promise<LoadedModel> {
  ort.env.wasm.wasmPaths = options.onnxWASMBasePath
  if (options.ortConfig !== undefined) {
    options.ortConfig(ort)
  }

  const modelURL = options.baseAssetPath + modelFiles[options.model]
  // v5 and v6 differ only in weights, so they share an implementation.
  const modelFactory: ModelFactory =
    options.model === "legacy" ? SileroLegacy.new : Silero.new

  let model: Model
  try {
    model = await modelFactory(ort, () => defaultModelFetcher(modelURL))
  } catch (e) {
    console.error(`Encountered an error while loading model file ${modelURL}`)
    throw e
  }

  const frameSamples = options.model === "legacy" ? 1536 : 512
  return { model, frameSamples, msPerFrame: frameSamples / 16 }
}
